import type { IconName } from '@act/storybook';
import type { NeedsIssueDto } from '@act/shared/data-transfer-objects';

/** Default icon for resource cards when the need issue is unknown or unmapped. */
export const DEFAULT_RESOURCE_CARD_ICON: IconName = 'ListMagnifyingGlass';

const HOUSING_ISSUE_KEYS = new Set<string>([
  'issue_homelessness',
  'issue_paying-for-housing-expenses',
  'issue_maintenance-and-repairs',
  'issue_cleaning-and-organizing',
  'issue_pest-infestation',
  'issue_safety',
  'issue_paying-for-utilities',
  'issue_lack-of-utilities',
  'issue_telephone-service-unavailable',
  'issue_internet-service-unavailable',
]);

const FOOD_SECURITY_ISSUE_KEYS = new Set<string>([
  'issue_paying-for-food',
  'issue_access-to-food',
  'issue_lack-of-food',
  'issue_diet-and-nutrition',
  'issue_insufficient-welfare-support',
]);

const TRANSPORTATION_ISSUE_KEYS = new Set<string>([
  'issue_paying-for-transportation',
  'issue_unreliable-transportation',
  'issue_lack-of-transportation',
]);

const EMOTIONAL_HEALTH_ISSUE_KEYS = new Set<string>([
  'issue_dependent-relative-needing-care',
  'issue_substance-use-in-family',
  'issue_family-illness',
  'issue_divorce',
  'issue_domestic-violence',
  'issue_social-isolation',
]);

const PHYSICAL_HEALTH_ISSUE_KEYS = new Set<string>([
  'issue_health-insurance-coverage',
  'issue_finding-a-physician',
  'issue_medication-access',
  'issue_mobility-and-physical-activity',
  'issue_dme-access',
  'issue_paying-for-medical-care',
  'issue_health-literacy',
]);

const EMPLOYMENT_EDUCATION_ISSUE_KEYS = new Set<string>([
  'issue_unemployment',
  'issue_change-of-job',
  'issue_literacy',
  'issue_language-barrier',
]);

const OTHER_BASIC_NEEDS_ISSUE_KEYS = new Set<string>(['issue_paying-for-clothing']);

/**
 * Returns the IconLibrary name for a member need issue key.
 *
 * @param issueKey - Needs metadata issue key (e.g. `issue_lack-of-food`).
 * @returns Icon name for the resource card decorative glyph.
 */
export function getResourceCardIconForNeedIssue(
  issueKey?: string | null
): IconName {
  if (!issueKey) {
    return DEFAULT_RESOURCE_CARD_ICON;
  }

  if (HOUSING_ISSUE_KEYS.has(issueKey)) {
    return 'HouseLine';
  }
  if (FOOD_SECURITY_ISSUE_KEYS.has(issueKey)) {
    return 'Carrot';
  }
  if (TRANSPORTATION_ISSUE_KEYS.has(issueKey)) {
    return 'Car';
  }
  if (EMOTIONAL_HEALTH_ISSUE_KEYS.has(issueKey)) {
    return 'HeartHand';
  }
  if (PHYSICAL_HEALTH_ISSUE_KEYS.has(issueKey)) {
    return 'Heartbeat';
  }
  if (EMPLOYMENT_EDUCATION_ISSUE_KEYS.has(issueKey)) {
    return 'Briefcase';
  }
  if (OTHER_BASIC_NEEDS_ISSUE_KEYS.has(issueKey)) {
    return DEFAULT_RESOURCE_CARD_ICON;
  }

  return DEFAULT_RESOURCE_CARD_ICON;
}

export interface ResourceCardNeedIconOptions {
  /** Resolved needs metadata issue key. */
  needIssueKey?: string;
  /** When only the issue id is known, resolved via `issues`. */
  needIssueId?: number;
  /** Needs metadata issues list for id → key lookup. */
  issues?: NeedsIssueDto[];
}

/**
 * Resolves the issue key from optional id + metadata, then maps to a card icon.
 *
 * @param options - Issue key or id with metadata issues for lookup.
 * @returns Icon name for the resource card decorative glyph.
 */
export function getResourceCardIconForNeed(
  options?: ResourceCardNeedIconOptions
): IconName {
  const issueKey =
    options?.needIssueKey ??
    (options?.needIssueId != null && options.issues
      ? options.issues.find(issue => issue.id === options.needIssueId)?.key
      : undefined);

  return getResourceCardIconForNeedIssue(issueKey);
}
